import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  AccessControl,
  Button,
  DataTableWithoutCheckbox,
  FieldSelect,
  FieldTextInput,
  hasAccess,
  Modal,
  PERMISSION_KEYS,
  PERMISSION_TYPES,
  ToastBar,
  ValidationError,
} from '../../components';
import { SpecialPostsService } from '../../services';
import type { SpecialPostStatus, SpecialPostType, SpecialPostTypePage } from '../../types/specialPosts';
import { dateFormatting } from '../../utils/dateMethods';
import editIcon from '../../assets/svg/edit.svg';
import activateIcon from '../../assets/svg/unblock.svg';
import deactivateIcon from '../../assets/svg/block.svg';
import deleteIcon from '../../assets/svg/delete.svg';
import StatusChip from '../EnterVice/parts/StatusChip';
import ConfirmDialog from '../EnterVice/parts/ConfirmDialog';
import { describeError } from '../EnterVice/parts/errors';
import '../LayoutWrapperMain/LayoutWrapperMain.module.scss';
import '../EnterVice/entervice.scss';

type DialogKind = 'activate' | 'deactivate' | 'delete';
type DialogState = { kind: 'none' } | { kind: DialogKind; item: SpecialPostType };

interface FilterValues {
  search: string;
  status: string;
}

interface ListFilter {
  page: number;
  rowsPerPage: number;
  status: SpecialPostStatus | '';
  search: string;
}

const STATUS_OPTIONS = [
  { key: 'all', label: 'All statuses' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

const domeName = (item: SpecialPostType) => (item.dome ? item.dome.name : 'its dome');

const DIALOG_COPY: Record<DialogKind, { title: string; confirm: string; danger?: boolean; message: (item: SpecialPostType) => string }> = {
  activate: {
    title: 'Activate special post type',
    confirm: 'Activate',
    message: (item) => `Turn on “${item.name}”? New ${item.name} posts go into “${domeName(item)}”, and the dome shows in Explore.`,
  },
  deactivate: {
    title: 'Deactivate special post type',
    confirm: 'Deactivate',
    message: (item) => `Turn off “${item.name}”? “${domeName(item)}” is hidden, and new ${item.name} posts use the normal dome picker again. Existing posts stay where they are.`,
  },
  delete: {
    title: 'Delete special post type',
    confirm: 'Delete',
    danger: true,
    message: (item) => `Delete “${item.name}” and its dome “${domeName(item)}”? The key and the dome name can be used again afterwards.`,
  },
};

/** `/specialposts`: special post types and the dome each one owns (SCRUM-247). */
const SpecialPosts: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ListFilter>({
    page: 0, rowsPerPage: 10, status: '', search: '',
  });
  const [list, setList] = useState<SpecialPostTypePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  const [reloadKey, setReloadKey] = useState(0);
  const filterForm = useForm<FilterValues>({ defaultValues: { search: '', status: 'all' } });

  const canEdit = hasAccess(PERMISSION_KEYS.SPECIAL_POSTS, PERMISSION_TYPES.EDIT);
  const canDelete = hasAccess(PERMISSION_KEYS.SPECIAL_POSTS, PERMISSION_TYPES.DELETE);
  const showActions = canEdit || canDelete;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    SpecialPostsService.list({
      pageNo: filter.page + 1,
      limit: filter.rowsPerPage,
      status: filter.status || undefined,
      search: filter.search || undefined,
    })
      .then((page) => {
        if (!cancelled) setList(page);
      })
      .catch((e) => {
        if (!cancelled) setError(describeError(e, 'Failed to load special post types.').message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter, reloadKey]);

  const reload = () => setReloadKey((key) => key + 1);

  const onFilter = (values: FilterValues) => {
    setFilter({
      ...filter,
      page: 0,
      search: values.search.trim(),
      status: values.status === 'all' ? '' : (values.status as SpecialPostStatus),
    });
  };

  const onResetFilter = () => {
    filterForm.reset({ search: '', status: 'all' });
    setFilter({ ...filter, page: 0, search: '', status: '' });
  };

  const closeDialog = () => setDialog({ kind: 'none' });

  const runDialogAction = async () => {
    if (dialog.kind === 'none') return;
    const { item } = dialog;
    switch (dialog.kind) {
      case 'activate':
        await SpecialPostsService.setStatus(item._id, 'active');
        setToast(`“${item.name}” is active.`);
        break;
      case 'deactivate':
        await SpecialPostsService.setStatus(item._id, 'inactive');
        setToast(`“${item.name}” deactivated.`);
        break;
      case 'delete':
        await SpecialPostsService.remove(item._id);
        setToast(`“${item.name}” deleted.`);
        break;
      default:
        break;
    }
    closeDialog();
    reload();
  };

  const renderActions = (item: SpecialPostType) => (
    <div className="ev-actions">
      <AccessControl permissionKey={PERMISSION_KEYS.SPECIAL_POSTS} accessType={PERMISSION_TYPES.EDIT}>
        <img
          src={editIcon}
          role="presentation"
          alt="edit"
          title="Edit"
          onClick={() => navigate(`/specialposts/editSpecialPost/${item._id}`)}
        />
      </AccessControl>
      <AccessControl permissionKey={PERMISSION_KEYS.SPECIAL_POSTS} accessType={PERMISSION_TYPES.EDIT}>
        {item.status === 'active' ? (
          <img
            src={deactivateIcon}
            role="presentation"
            alt="deactivate"
            title="Deactivate"
            onClick={() => setDialog({ kind: 'deactivate', item })}
          />
        ) : (
          <img
            src={activateIcon}
            role="presentation"
            alt="activate"
            title="Activate"
            onClick={() => setDialog({ kind: 'activate', item })}
          />
        )}
      </AccessControl>
      <AccessControl permissionKey={PERMISSION_KEYS.SPECIAL_POSTS} accessType={PERMISSION_TYPES.DELETE}>
        <img
          src={deleteIcon}
          role="presentation"
          alt="delete"
          title="Delete"
          onClick={() => setDialog({ kind: 'delete', item })}
        />
      </AccessControl>
    </div>
  );

  // WHY: DataTableWithoutCheckbox renders row keys in insertion order (skipping `id`), so this order is the column order.
  const rows = (list ? list.data : []).map((item) => ({
    id: item._id,
    icon: item.dome && item.dome.selectedImage
      ? <img className="ev-thumb" src={item.dome.selectedImage} alt={domeName(item)} />
      : <div className="ev-thumb ev-thumb--empty">none</div>,
    name: (
      <div className="ev-cell-name">
        <span className="ev-name">{item.name}</span>
        <span className="ev-key">{item.key}</span>
      </div>
    ),
    dome: domeName(item),
    status: <StatusChip status={item.status} />,
    rank: item.dome && item.dome.rank ? item.dome.rank : '—',
    createdAt: dateFormatting(item.createdAt),
    ...(showActions ? { action: renderActions(item) } : {}),
  }));

  const headCells = [
    { id: 'icon', label: 'Dome icon', sort: false },
    { id: 'name', label: 'Special post type', sort: false },
    { id: 'dome', label: 'Dome', sort: false },
    { id: 'status', label: 'Status', sort: false },
    { id: 'rank', label: 'Dome rank', sort: false },
    { id: 'createdAt', label: 'Added on', sort: false },
    ...(showActions ? [{ id: 'action', label: 'Action', sort: false }] : []),
  ];

  return (
    <>
      <Modal
        id="special-posts-dialog"
        isOpen={dialog.kind !== 'none'}
        onClose={closeDialog}
        onManageDisableScrolling={() => null}
        lightCloseButton={false}
      >
        {dialog.kind !== 'none' && (
          <ConfirmDialog
            title={DIALOG_COPY[dialog.kind].title}
            message={DIALOG_COPY[dialog.kind].message(dialog.item)}
            confirmLabel={DIALOG_COPY[dialog.kind].confirm}
            danger={DIALOG_COPY[dialog.kind].danger}
            onConfirm={runDialogAction}
            onClose={closeDialog}
          />
        )}
      </Modal>

      {toast && <ToastBar message={toast} handleCloseMethod={() => setToast('')} />}

      <div>
        <div className="topButton">
          <AccessControl permissionKey={PERMISSION_KEYS.SPECIAL_POSTS} accessType={PERMISSION_TYPES.ADD}>
            <Button buttonType="button" onClick={() => navigate('/specialposts/addSpecialPost')}>+ New Special Post Type</Button>
          </AccessControl>
        </div>

        <form className="cardBg ev-filter" onSubmit={filterForm.handleSubmit(onFilter)}>
          <FieldTextInput
            id="search"
            name="search"
            formName="specialPostsFilter"
            className="textInput"
            type="text"
            formProps={filterForm}
            label="Search by name or key"
          />
          <FieldSelect
            id="status"
            name="status"
            label="Status"
            options={STATUS_OPTIONS}
            formProps={filterForm}
            defaultValue="all"
          />
          <div className="ev-filter-actions">
            <Button buttonType="submit">Search</Button>
            <Button buttonType="button" className="ev-btn-ghost" onClick={onResetFilter}>Reset</Button>
          </div>
        </form>

        {error && <ValidationError className="validation_error" error={error} />}

        <div className="dataTableCover">
          <DataTableWithoutCheckbox
            headCells_={headCells}
            rows_={rows}
            rowClick={() => null}
            selected={[]}
            setSelected={() => null}
            setSelectedRows={() => null}
            page={filter.page}
            rowsPerPage={filter.rowsPerPage}
            loading={loading}
            handleChangePage={(_event: unknown, newPage: number) => setFilter({ ...filter, page: newPage })}
            handleChangeRowsPerPage={(event: React.ChangeEvent<HTMLInputElement>) => setFilter({
              ...filter, page: 0, rowsPerPage: parseInt(event.target.value, 10),
            })}
            handleSort={() => null}
            count={list ? list['Pagination-Count'] : 0}
          />
        </div>
      </div>
    </>
  );
};

export default SpecialPosts;
